import { createContext, useContext, useState } from 'react';
import posthog from 'posthog-js';
import ReactGA from 'react-ga4';

import { Assessment } from '../utils/assessment';
import { apneaQuestions, getBMI } from '../utils/constants';
import { useFeatureFlagEnabled, useFeatureFlagVariantKey } from 'posthog-js/react';

const initialState = {
    answers: {},
    apneaScore: 0,
    assessmentStep: 'questions', // questions, calculating, result, create-account
    currentQuestionIndex: apneaQuestions.findIndex((q) => q.answerKey === 'doesSnoreLoudly'), //default to the doesSnoreLoudly question
    hasHighRisk: false,
    homeSleepStudyStatus: null,
    isCoachingPath: false, // anthem patients on coaching path
    partnerId: null,
    partnerType: null,
    previouslyDiagnosed: false,
    questions: apneaQuestions,
};

const questionIndexMap = new Map(
    apneaQuestions.map((question, index) => [question.answerKey, index]),
);

function determineIfHighRiskSelections(answers) {
    return Array.isArray(answers) && answers.length > 0 && answers.indexOf('NONE') < 0;
}

const AssessmentContext = createContext();

export function AssessmentContextProvider({ children }) {
    //Allow users to sign up if their score is 1 or 2
    const allowLowScores = useFeatureFlagEnabled('low-scores');
    //Allow users to email their stopbang results: variants 'test' and 'control'
    const emailResultsVariant = useFeatureFlagVariantKey('email-results');

    const [state, setState] = useState(initialState);

    // Reset the assessment to initial state
    function reset() {
        setState(initialState);
        posthog?.capture('Reset assessment');
    }

    /**
     * Updates the assessment state with the selected partner's information and logs relevant analytics events.
     *
     * @param {Object} partner - The partner object containing partner details.
     * @param {string|number} partner.id - The unique identifier for the partner.
     * @param {string} partner.type - The type/category of the partner (e.g., 'SLEEP_NUMBER').
     */
    function setPartner({ partnerId, partnerType }) {
        //If they are a sleep number referral, ask them the sleep number question
        if (partnerType === 'SLEEP_NUMBER') {
            setState((prevState) => ({
                ...prevState,
                questions: prevState.questions.map((q) =>
                    q.answerKey === 'sleepNumberBreathIqIrregularities' ? { ...q, skip: false } : q,
                ),
            }));
            posthog?.capture('Enabled Sleep Number questions in assessment');
        }

        setState((prevState) => ({
            ...prevState,
            partnerId,
            partnerType,
        }));
    }

    // Accepts the answerKey string for the current question
    function previousQuestion(answerKey) {
        setState((prevState) => {
            const answers = { ...prevState.answers };
            if (!['weCanHelp', 'needNewSleepStudy'].includes(answerKey)) {
                answers[answerKey] = null;
            }
            let prevIndex = prevState.currentQuestionIndex - 1;
            while (prevState.questions[prevIndex]?.skip) prevIndex--;
            posthog?.capture('Proceeded to previous question', { from: answerKey });
            return {
                ...prevState,
                currentQuestionIndex: prevIndex,
                answers,
            };
        });
    }

    // Accepts the answerKey string for the current question
    function nextQuestion(answerKey) {
        setState((prevState) => {
            //If they have left the questions step, do nothing
            if (prevState.assessmentStep !== 'questions') {
                return { ...prevState };
            }

            const currentQuestionIndex = prevState.questions.findIndex(
                (q) => q.answerKey === answerKey,
            );

            let nextIndex = currentQuestionIndex + 1;
            while (prevState.questions[nextIndex]?.skip) nextIndex++;
            posthog?.capture('Proceeded to next question', { from: answerKey });

            //If you have finished the question set, proceed to the next assessment step
            if (nextIndex === prevState.questions.length) {
                return { ...prevState, assessmentStep: 'anythingElse' };
            }

            //Otherwise proceed to the next index
            return {
                ...prevState,
                currentQuestionIndex: nextIndex,
            };
        });
    }

    // Set the assessment step
    function setAssessmentStep(payload) {
        setState((prevState) => ({
            ...prevState,
            assessmentStep: payload,
        }));
    }

    // Set the sleep study status
    function setSleepStudyStatus(payload) {
        setState((prevState) => ({
            ...prevState,
            homeSleepStudyStatus: payload,
        }));
    }

    //Set an answer for a question
    function setAnswer({ answer, answerKey }) {
        setState((prevState) => {
            let questions = prevState.questions.map((q) => ({ ...q }));
            const answers = { ...prevState.answers };
            let previouslyDiagnosed = prevState.previouslyDiagnosed;
            let hasHighRisk = prevState.hasHighRisk;
            let apneaScore = prevState.apneaScore;
            let assessmentStep = prevState.assessmentStep;

            // If they have come through sleep number, show them this question
            const snIndex = questionIndexMap.get('sleepNumberBreathIqIrregularities');
            if (snIndex !== undefined && snIndex !== -1) {
                questions[snIndex].skip = prevState.partnerType !== 'SLEEP_NUMBER';
            }

            if (answerKey === 'previouslyDiagnosedWithSleepApnea') {
                // Reset all questions' skip to their initial state
                questions = apneaQuestions.map((q) => ({ ...q }));

                // If the user is previously diagnosed, ask if they have gained or lost 20 pounds
                if (answer) {
                    const gained20LbsIndex = questionIndexMap.get('gained20Lbs');
                    if (gained20LbsIndex !== undefined && gained20LbsIndex !== -1) {
                        questions[gained20LbsIndex].skip = false;
                    }
                }

                const questionsForPreviouslyDiagnosed = [
                    'previouslyDiagnosedWithSleepApnea',
                    'howBroughtToGem',
                    'weCanHelp',
                    'hasPriorStudy',
                    'needNewSleepStudy',
                ];

                // Find all of the previously diagnosed questions
                const indicesForQuestionsForPreviouslyDiagnosed =
                    questionsForPreviouslyDiagnosed.map((key) => questionIndexMap.get(key));

                // Skip them if the user is not previously diagnosed
                indicesForQuestionsForPreviouslyDiagnosed.forEach((index) => {
                    if (index !== undefined && index !== -1) {
                        questions[index].skip = !answer;
                    }
                });

                const stopBreathingQuestions = ['doesStopBreathing', 'doesStopBreathing2'];

                // Find the stop breathing questions
                const indicesForStopBreathingQuestions = stopBreathingQuestions.map((key) =>
                    questionIndexMap.get(key),
                );

                // And skip them if the user is previously diagnosed
                indicesForStopBreathingQuestions.forEach((index) => {
                    if (index !== undefined && index !== -1) {
                        questions[index].skip = answer;
                    }
                });

                // Answer does stop breathing based on previously diagnosed answer
                answers['doesStopBreathing'] = answer;

                // Set to state whether they were previously diagnosed
                previouslyDiagnosed = answer;
            }

            // Determinations when the user tells us how they were brought to GEM
            else if (answerKey === 'howBroughtToGem') {
                // If the user is looking for coaching with their current CPAP, don't skip the extra exclusionary questions
                const highRiskSelections2Index = questionIndexMap.get('highRiskSelections2');
                if (highRiskSelections2Index !== undefined && highRiskSelections2Index !== -1) {
                    questions[highRiskSelections2Index].skip = answer !== 'STRUGGLE_WITH_CPAP';
                }

                // If the user needs a new sleep test, skip the hasPriorStudy and needNewSleepStudy questions
                ['hasPriorStudy', 'needNewSleepStudy'].forEach((key) => {
                    const index = questionIndexMap.get(key);
                    if (index !== undefined && index !== -1) {
                        questions[index].skip = answer === 'NEED_NEW_SLEEP_TEST';
                    }
                });
            }

            // If they answer yes to the first question about stopping breathing, skip the second question.
            else if (answerKey === 'doesStopBreathing') {
                if (prevState.currentQuestionIndex + 1 < questions.length) {
                    questions[prevState.currentQuestionIndex + 1].skip = answer;
                }
            }

            // If the user has a prior study, skip the need new sleep study question
            else if (answerKey === 'hasPriorStudy') {
                const needNewSleepStudyIndex = questionIndexMap.get('needNewSleepStudy');
                if (needNewSleepStudyIndex !== undefined && needNewSleepStudyIndex !== -1) {
                    questions[needNewSleepStudyIndex].skip = answer;
                }
            }

            // If they have gained or lost 20 pounds, show them the situationChanged question
            else if (answerKey === 'gained20Lbs') {
                const situationChangedIndex = questionIndexMap.get('situationChanged');
                if (situationChangedIndex !== undefined && situationChangedIndex !== -1) {
                    questions[situationChangedIndex].skip =
                        !answer || answers.howBroughtToGem === 'STRUGGLE_WITH_CPAP';
                }
            }

            // Set the answer to state for calculation and submission, capture analytics
            if (answerKey) {
                answers[answerKey] = answer;

                posthog?.capture('Answered a Question', {
                    question: answerKey,
                    answer: answer?.toString(),
                });
            }

            // Capture values for Google Analytics
            if (typeof answerKey !== 'undefined') {
                switch (answerKey) {
                    case 'heightInches':
                    case 'weightLbs':
                        ReactGA.event({
                            category: 'User',
                            action: 'Answered a Question',
                            label: `${answerKey}_true`,
                        });
                        break;
                    case 'highRiskSelections':
                        ReactGA.event({
                            category: 'User',
                            action: 'Answered a Question',
                            label: `${answerKey}_${Array.isArray(answer) ? answer.join(',') : answer}`,
                        });
                        break;
                    default:
                        ReactGA.event({
                            category: 'User',
                            action: 'Answered a Question',
                            label: `${answerKey}_${answers[answerKey]}`,
                        });
                }
            }

            // Calculate age, bmi, and compute stopbang score to determine if they have apnea based on current questions
            const bmi = getBMI(answers.weightLbs, answers.heightInches);

            // Recalculate the apnea score based on the answers
            apneaScore = Assessment.computeStopBangScore(answers, bmi);

            // On each of the two high risk selection questions, determine if they have high risk selections
            if (answerKey === 'highRiskSelections' || answerKey === 'highRiskSelections2') {
                //Capture any high risk selections they might have chosen
                answer.forEach((answer) =>
                    posthog?.capture('High risk selection', { value: answer }),
                );

                //If they are an insomnia referral and only answered INSOMNIA, proceed to the insomnia step
                if (
                    answer.length === 1 &&
                    answer.some((ans) => ans === 'INSOMNIA') &&
                    prevState.partnerType === 'INSOMNIA'
                ) {
                    assessmentStep = 'insomnia';
                    posthog?.capture('Shown insomnia referral');
                } else {
                    hasHighRisk = determineIfHighRiskSelections(answer);
                }
            } else {
                hasHighRisk = bmi >= 45;
            }

            //Show low score additional question for apneaScore of under 3 when sleep number referral or feature flag enabled
            if (allowLowScores || prevState.answers.sleepNumberBreathIqIrregularities) {
                const lowScoreAdditionsQuestionIndex = questionIndexMap.get('lowScoreAdditions');
                state.questions[lowScoreAdditionsQuestionIndex].skip = apneaScore > 2;
            }

            if (hasHighRisk) {
                // If they have high risk selections proceed to result screen
                assessmentStep = 'result';
            }

            return {
                ...prevState,
                questions,
                answers,
                previouslyDiagnosed,
                hasHighRisk,
                apneaScore,
                assessmentStep,
            };
        });
    }

    function finishAssessment() {
        setState((prevState) => {
            let assessmentStep = prevState.assessmentStep;

            // If they weren't previously diagnosed, show them the calculation animation
            if (!prevState.previouslyDiagnosed) {
                assessmentStep = 'calculating';
            } else if (
                // Otherwise take them straight to account creation if their BMI is under 45 and they are not on the coaching path
                !prevState.hasHighRisk &&
                !prevState.isCoachingPath
            ) {
                assessmentStep = 'create-account';
            } else {
                // Or show them they have high risk in result step
                assessmentStep = 'result';
            }

            // Capture the assessment completion
            ReactGA.event({
                category: 'User',
                action: 'Completed assessment',
            });

            return { ...prevState, assessmentStep };
        });
    }

    const currentQuestionData = state.questions
        ? state.questions[state.currentQuestionIndex]
        : null;

    //Filter out coaching selection
    const noCoachingChoice = currentQuestionData?.choices?.filter(
        (choice) => choice.value !== 'STRUGGLE_WITH_CPAP',
    );

    //Filter out insomnia question
    const noInsomniaChoice = currentQuestionData?.choices?.filter(
        (choice) => choice.value !== 'INSOMNIA',
    );

    const questionChoices =
        //If they are on the coaching path, eliminate the insomnia question
        state.isCoachingPath && currentQuestionData?.answerKey === 'highRiskSelections'
            ? noInsomniaChoice
            : //If they are not a sleep support partner, eliminate the coaching choice
              state.partnerType !== 'SLEEP_SUPPORT'
              ? noCoachingChoice
              : //Otherwise give them the default choices
                currentQuestionData?.choices;

    const contextValue = {
        ...state,
        currentQuestionData,
        emailResultsVariant,
        questionChoices,
        finishAssessment,
        setAnswer,
        nextQuestion,
        previousQuestion,
        setAssessmentStep,
        reset,
        setPartner,
        setSleepStudyStatus,
    };

    return <AssessmentContext.Provider value={contextValue}>{children}</AssessmentContext.Provider>;
}

export function useAssessmentContext() {
    const context = useContext(AssessmentContext);
    if (!context) {
        throw new Error('useAssessment must be used within an AssessmentProvider');
    }
    return context;
}
